import { useEffect, useState } from "react";
import api, { formatErr, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import {
  UploadSimple,
  Paperclip,
  CheckCircle,
  FilePdf,
  X,
} from "@phosphor-icons/react";

function defaultDeliveryNote() {
  const now = new Date();

  const formatted = now.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  return `Your order was successfully delivered on ${formatted}.`;
}

// "/uploads/receipts/x.jpg" -> "http://host:8001/uploads/receipts/x.jpg"
function toFullUrl(path) {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return `${BACKEND_URL}${path.startsWith("/") ? "" : "/"}${path}`;
}

function isPdfPath(path) {
  return /\.pdf($|\?)/i.test(path || "");
}

export default function DeliveryUploadBox({
  saleId,
  invoiceNo,
  deliveryStatus,
  receiptFilePath,
  onConfirmed,
}) {
  const [file, setFile] = useState(null);
  const [localPreview, setLocalPreview] = useState(""); // chuni hui file ka preview (upload se pehle)
  const [savedPath, setSavedPath] = useState(receiptFilePath || ""); // server pe saved receipt
  const [note, setNote] = useState(defaultDeliveryNote());
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(!receiptFilePath);
  const [zoomOpen, setZoomOpen] = useState(false);

  // Parent se naya receiptFilePath aaye (dusri row khuli) to sync karo
  useEffect(() => {
    setSavedPath(receiptFilePath || "");
    setExpanded(!receiptFilePath);
  }, [receiptFilePath, saleId]);

  // Object URL memory leak na ho
  useEffect(() => {
    return () => {
      if (localPreview) URL.revokeObjectURL(localPreview);
    };
  }, [localPreview]);

  const ALLOWED_TYPES = ["image/jpeg", "image/png", "application/pdf"];
  const MAX_SIZE_MB = 10;

  const clearSelectedFile = () => {
    setFile(null);
    setLocalPreview("");
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!ALLOWED_TYPES.includes(selectedFile.type)) {
      toast.error("Only JPG, PNG, or PDF files are allowed");
      e.target.value = "";
      clearSelectedFile();
      return;
    }

    if (selectedFile.size > MAX_SIZE_MB * 1024 * 1024) {
      toast.error(`File must be under ${MAX_SIZE_MB}MB`);
      e.target.value = "";
      clearSelectedFile();
      return;
    }

    setFile(selectedFile);
    setLocalPreview(
      selectedFile.type.startsWith("image/") ? URL.createObjectURL(selectedFile) : ""
    );
    e.target.value = ""; // same file dobara chunne pe bhi onChange chale
  };

  const submit = async () => {
    if (!saleId) {
      toast.error("Sale reference is missing");
      return;
    }
    if (!file) {
      toast.error("Please select a receipt file first");
      return;
    }

    setBusy(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("note", note);

      const response = await api.post(
        `/sales/${encodeURIComponent(saleId)}/delivery-confirmation`,
        formData
      );

      toast.success("Receipt saved successfully");

      const sale = response?.data?.sale;
      if (sale?.receipt_file_path) setSavedPath(sale.receipt_file_path);

      setExpanded(false);
      clearSelectedFile();

      if (sale) onConfirmed?.(sale);
    } catch (error) {
      console.error("Delivery confirmation error:", error);
      toast.error(formatErr(error));
    } finally {
      setBusy(false);
    }
  };

  // ---------- Saved receipt: preview ----------
  if (!expanded) {
    const fullUrl = toFullUrl(savedPath);
    const pdf = isPdfPath(savedPath);

    return (
      <div className="rounded-md border border-border bg-muted/40 p-3 space-y-3 text-sm">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-signal">
            <CheckCircle size={16} weight="fill" />
            <span>Receipt saved</span>
          </div>
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="text-xs text-muted-foreground hover:text-foreground underline"
          >
            Replace
          </button>
        </div>

        {fullUrl && !pdf && (
          <button
            type="button"
            onClick={() => setZoomOpen(true)}
            className="block w-full overflow-hidden rounded-md border border-border bg-card"
            title="Click to view full size"
            data-testid={`receipt-preview-${invoiceNo}`}
          >
            <img
              src={fullUrl}
              alt={`Receipt for invoice ${invoiceNo}`}
              className="w-full max-h-72 object-contain"
              loading="lazy"
            />
          </button>
        )}

        {fullUrl && pdf && (
          <a
            href={fullUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 hover:bg-muted"
          >
            <FilePdf size={20} />
            <span className="underline">Open PDF receipt</span>
          </a>
        )}

        {/* Full-size image viewer */}
        {zoomOpen && fullUrl && !pdf && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-4"
            onClick={() => setZoomOpen(false)}
          >
            <button
              type="button"
              onClick={() => setZoomOpen(false)}
              className="absolute top-4 right-4 rounded-full bg-white/15 p-2 text-white hover:bg-white/25"
              aria-label="Close preview"
            >
              <X size={20} weight="bold" />
            </button>
            <img
              src={fullUrl}
              alt={`Receipt for invoice ${invoiceNo}`}
              className="max-h-full max-w-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </div>
    );
  }

  // ---------- Upload form ----------
  return (
    <div
      className="border border-border rounded-md p-4 space-y-3 bg-card"
      data-testid={`delivery-box-${invoiceNo}`}
    >
      <label className="block cursor-pointer">
        <div className="flex items-center gap-2 h-11 px-3 rounded-md border border-dashed border-border hover:bg-muted text-sm text-muted-foreground">
          <Paperclip size={16} />
          <span className="truncate">
            {file ? file.name : "Click to upload document / receipt (JPG, PNG, PDF)"}
          </span>
        </div>
        <input
          type="file"
          accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
          className="hidden"
          onChange={handleFileChange}
          data-testid={`receipt-file-input-${invoiceNo}`}
        />
      </label>

      {/* Upload se pehle preview */}
      {file && (
        <div className="relative rounded-md border border-border bg-muted/40 p-2">
          <button
            type="button"
            onClick={clearSelectedFile}
            className="absolute top-2 right-2 rounded-full bg-card/90 p-1 text-muted-foreground hover:text-foreground"
            aria-label="Remove selected file"
          >
            <X size={14} weight="bold" />
          </button>
          {localPreview ? (
            <img
              src={localPreview}
              alt="Selected receipt preview"
              className="w-full max-h-60 object-contain rounded"
            />
          ) : (
            <div className="flex items-center gap-2 px-2 py-3 text-sm">
              <FilePdf size={20} />
              <span className="truncate">{file.name}</span>
            </div>
          )}
        </div>
      )}

      <label className="block">
        <span className="text-xs uppercase tracking-widest text-muted-foreground">
          Delivery Note
        </span>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          className="w-full mt-1.5 px-3 py-2 rounded-md border border-border bg-card focus:outline-none focus:ring-2 focus:ring-signal/40 text-sm resize-none"
          data-testid={`delivery-note-input-${invoiceNo}`}
        />
      </label>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={submit}
          disabled={busy || !file}
          data-testid={`save-receipt-button-${invoiceNo}`}
          className="signal-btn h-9 px-4 rounded-md text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60"
        >
          <UploadSimple size={16} weight="bold" />
          {busy ? "Saving..." : "Save Receipt"}
        </button>

        {savedPath && (
          <button
            type="button"
            onClick={() => {
              clearSelectedFile();
              setExpanded(false);
            }}
            className="text-xs text-muted-foreground hover:text-foreground underline"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}
